import Tree, { deepClone } from "./Tree";
import api from "../api";
import { Direction, TreeDefaultNodeIds } from "../../utils/constants";
import { TeamTypes } from "../../utils/icons";
import { v4 as uuidv4 } from "uuid";

export default {
  namespaced: true,
  state: {
    persistTimeout: null,
    tree: null,
    activeItem: {
      id: null,
      editMode: false,
    },
    visibleItemIds: [],
    addMemberDialog: {
      show: false,
      id: null,
      editMode: false,
    },
    isLoading: false,
    teamsHashTable: {},
  },
  mutations: {},
  actions: {
    setTeamsHashTable({ commit, state, dispatch }) {
      state.teamsHashTable = state.tree.getHashTable(state.tree.allData);
    },
    setActiveTeams({ commit, state, dispatch }, id) {
      if (!id) id = TreeDefaultNodeIds.Root;

      state.tree.setActiveSubTree(id);
      dispatch("calculateVisibleItemIds");
      dispatch("setActiveItem", { id });
    },
    async getTeamById({ commit }, teamId) {
      try {
        const team = await api.get(`teams/${teamId}/name`);
        return team.data;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    async load({ commit, state, dispatch }) {
      try {
        if (state.isLoading) return;
        state.isLoading = true;

        const resp = await api.get(`teams?t=${new Date().getTime()}`);

        const tree = {
          id: TreeDefaultNodeIds.Root,
          title: "root",
          expanded: true,
          children: resp.data.map((item) => ({
            id: item.id,
            title: item.name,
            type: TeamTypes.Team,
            expanded: true,
            children: item.members.map((member) => ({
              id: uuidv4(),
              title: member.email,
              email: member.email,
              type: member.role,
              expanded: true,
              children: [],
            })),
          })),
        };

        state.tree = new Tree(tree);

        await dispatch("calculateVisibleItemIds");
        await dispatch("setTeamsHashTable");
        dispatch("smartSearch/loadTeams", null, { root: true });

        state.isLoading = false;
      } catch (error) {
        console.log(error);
        state.isLoading = false;
      }
    },
    toggleExpanded({ commit, state, dispatch }, { id, expanded = null }) {
      const item = state.tree.getNode(state.tree.allData, id);
      if (!item) return;

      if (expanded === null) expanded = !item.expanded;

      state.tree.setValue(id, "expanded", expanded);

      dispatch("setActiveItem", { id });
      dispatch("calculateVisibleItemIds");
      dispatch("resetActiveContextMenu", null, { root: true });
    },
    async addTeam({ commit, state, dispatch }) {
      try {
        if (state.isLoading) return;
        state.isLoading = true;

        const id = uuidv4();
        const resp = await api.post(`teams`, { name: "New Team", id, itemId: id });

        const team = resp.data;
        state.tree.addNode(
          {
            id: team.id,
            itemId: team.itemId,
            title: team.name,
            expanded: true,
            type: TeamTypes.Team,
            children: team.members.map((member) => {
              const nodeId = uuidv4();
              return {
                id: nodeId,
                itemId: nodeId,
                title: member.email,
                email: member.email,
                type: member.role,
                expanded: true,
                children: [],
              };
            }),
          },
          TreeDefaultNodeIds.Root
        );
        dispatch("setActiveItem", { id: team.id, editMode: true });
        dispatch("calculateVisibleItemIds");
        dispatch("setTeamsHashTable");
        dispatch("smartSearch/loadTeams", null, { root: true });
      } catch (error) {
        console.log(error);
      } finally {
        state.isLoading = false;
      }
    },
    async removeTeam({ commit, state, dispatch }, { id }) {
      try {
        if (state.isLoading) return;
        state.isLoading = true;

        const team = state.tree.getNode(state.tree.allData, id);
        if (!team) {
          console.log("Team not found");
          return;
        }
        // Check if the team has any designs before deletion
        const response = await api.get(`designs/${id}/team-designs`);
        if (response.data.length > 0) {
          dispatch(
            "addToast",
            {
              severity: "warn",
              life: 3000,
              summary: "Cannot Delete",
              detail: "This team has existing designs and cannot be deleted.",
            },
            { root: true }
          );
          return;
        }
        // Remove team from the tree structure
        state.tree.removeNode(id);

        // Persist changes to the backend
        await api.delete(`teams/${id}`);

        // Update state
        dispatch("calculateVisibleItemIds");
        dispatch("setTeamsHashTable");
        dispatch("smartSearch/loadTeams", null, { root: true });

        dispatch(
          "addToast",
          {
            severity: "success",
            life: 3000,
            summary: "Deleted Successfully",
            detail: "Team deleted successfully.",
          },
          { root: true }
        );
      } catch (error) {
        console.log(error);
        dispatch(
          "addToast",
          {
            severity: "error",
            life: 3000,
            summary: "Failed To Delete",
            detail: "An error occurred while deleting the team.",
          },
          { root: true }
        );
      } finally {
        state.isLoading = false;
      }
    },
    async addMember({ commit, state, dispatch }, { teamId, email, role }) {
      const team = state.tree.getNode(state.tree.allData, teamId);

      if (team.children.find((item) => item.email === email))
        return dispatch(
          "addToast",
          {
            severity: "warn",
            life: 3000,
            summary: "Failed",
            detail: "This email already exists in this team.",
          },
          { root: true }
        );

      try {
        state.isLoading = true;

        const newMember = {
          id: uuidv4(),
          title: email,
          email: email,
          type: role,
          expanded: true,
          children: [],
        };
        state.tree.addNode(newMember, teamId);

        const members = [...team.children.map((item) => ({ email: item.email, role: item.type })), { email, role }];
        dispatch("setAddMemberDialog", { show: false });

        const resp = await api.patch(`teams/${team.id}`, {
          id: teamId,
          members,
        });
        dispatch("setTeamsHashTable");
        dispatch("smartSearch/loadTeams", null, { root: true });
      } catch (error) {
        console.log(error);
        return dispatch(
          "addToast",
          {
            severity: "error",
            life: 3000,
            summary: "Failed",
            detail: "Adding a New member to the team failed",
          },
          { root: true }
        );
      } finally {
        state.isLoading = false;
      }
    },
    async remove({ commit, state, dispatch }, { ids }) {
      try {
        const id = ids[0];
        const member = state.tree.getNode(state.tree.allData, id);
        if (member.type === TeamTypes.Owner) return;

        state.tree.removeNode(id);

        const teamId = member.parents[member.parents.length - 1];
        const team = state.tree.getNode(state.tree.allData, teamId);

        const members = team.children.map((item) => ({ email: item.email, role: item.type }));
        dispatch("setAddMemberDialog", { show: false });

        const resp = await api.patch(`teams/${teamId}`, {
          id: teamId,
          members,
        });
        dispatch(
          "addToast",
          {
            severity: "success",
            life: 3000,
            summary: "Deleted Successfully",
            detail: "Member deleted successfully.",
          },
          { root: true }
        );
        dispatch("setTeamsHashTable");
        dispatch("smartSearch/loadTeams", null, { root: true });
      } catch (error) {
        dispatch(
          "addToast",
          {
            severity: "error",
            life: 3000,
            summary: "Failed To Delete",
            detail: "Sorry Something went wrong",
          },
          { root: true }
        );
        console.log(error);
      }
    },
    async changeRole({ commit, state, dispatch }, { teamId, memberId, role }) {
      const team = state.tree.getNode(state.tree.allData, teamId);

      const newChildren = team.children.map((item) => {
        if (item.id === memberId) return { ...item, type: role };
        return item;
      });
      state.tree.setValue(teamId, "children", newChildren);

      const members = newChildren.map((item) => ({ email: item.email, role: item.type }));
      dispatch("setAddMemberDialog", { show: false });

      const resp = await api.patch(`teams/${team.id}`, {
        id: teamId,
        members,
      });
    },
    async rename({ commit, state, dispatch }, { id, title }) {
      try {
        if (state.isLoading) return;
        state.isLoading = true;

        state.tree.setValue(id, "title", title);

        // reset team getting edited
        dispatch("setActiveItem", { id, editMode: false });

        await api.patch(`teams/${id}`, {
          id,
          name: title,
        });
        // TODO: should create a better way if user rename from processes also it should effect all places
        await dispatch("processes/renameTeam", { id, itemId: id, title }, { root: true });
        await dispatch("grids/renameTeam", { id, itemId: id, title }, { root: true });
        await dispatch("businesses/renameTeam", { id, itemId: id, title }, { root: true });
        await dispatch("players/rename", { id, title }, { root: true });
        dispatch("setTeamsHashTable");
        dispatch("smartSearch/loadTeams", null, { root: true });
      } catch (error) {
        console.log(error);
      } finally {
        state.isLoading = false;
      }
    },
    sendMail({ commit, state, dispatch, rootState }, { id }) {
      const item = state.tree.getNode(state.tree.allData, id);

      const authUser = rootState.authUser;
      if (item.email === authUser.email) return;

      let recipients = item.children.filter((member) => member.email !== authUser.email).map((item) => item.email);
      if (item.type !== TeamTypes.Team) recipients.unshift(item.email);

      let emailUrl = "mailto:" + recipients.join(",");

      window.open(emailUrl, "_blank");
      dispatch("resetActiveContextMenu", null, { root: true });
    },
    async persist({ commit, state, dispatch }) {
      clearTimeout(state.persistTimeout);
      state.persistTimeout = setTimeout(async () => {
        try {
          let list = deepClone(state.tree.allData);

          state.tree.removeMetaData(list);

          const data = { list };
          // await api.patch("players", data);
        } catch (error) {
          console.log(error);
        }
      }, 500);
    },
    setActiveItem({ commit, state, dispatch }, { id, itemId, editMode = false }) {
      if (editMode) {
        dispatch("resetActiveContextMenu", null, { root: true });
      }
      state.activeItem = {
        id,
        itemId: itemId ?? id,
        editMode,
      };
    },
    resetActiveItem({ commit, state, dispatch }) {
      state.activeItem = {
        id: null,
      };
    },
    calculateVisibleItemIds({ commit, state, dispatch }) {
      state.visibleItemIds = state.tree.getVisibleNodeIds(state.tree.activeSubTree);
    },
    setAddMemberDialog({ commit, state, dispatch }, { show, id = null, editMode = false }) {
      dispatch("resetActiveContextMenu", null, { root: true });
      state.addMemberDialog = {
        show,
        id,
        editMode,
      };
    },
    bringIntoView({ commit, state, dispatch }, { id }) {
      state.tree.expandParentNodes(id);
      dispatch("calculateVisibleItemIds");
    },
  },
  getters: {
    getItemById: (state) => (id) => {
      return state.tree?.getNode(state.tree.allData, id);
    },
    teamId: (state) => (itemId) => {
      const activeItem = state.tree.getNode(state.tree.allData, itemId);
      return activeItem?.parents[1] ?? itemId;
    },
    isEditAllowed: (state) => (id) => {
      const node = state.tree.getNode(state.tree.allData, id);

      if (node.id === TreeDefaultNodeIds.Private) return false;
      return true;
    },
    isMoveUpAllowed: (state) => (id) => {
      const node = state.tree.getNode(state.tree.allData, id);
      const parentNodeId = node.parents[node.parents.length - 1];

      if (parentNodeId === TreeDefaultNodeIds.Root || parentNodeId === TreeDefaultNodeIds.Private) return false;
      return true;
    },
    isMoveDownAllowed: (state) => (id) => {
      const node = state.tree.getNode(state.tree.allData, id);

      if (node.id === TreeDefaultNodeIds.Private) return false;
      return true;
    },
    isMoveAllowed: (state) => (id, targetId) => {
      const target = state.tree.getNode(state.tree.allData, targetId);
      const targetParentId = target.parents[target.parents.length - 1];
      if (targetParentId === TreeDefaultNodeIds.Root) return false;

      return true;
    },
    search: (state) => (searchTerm) => {
      const items = state.tree?.filter(
        (item) => item.title.toLowerCase().includes(searchTerm.toLowerCase()) && item.type !== TeamTypes.Folder
      );
      return items;
    },
    getTeamNames: (state) => {
      const root = state.tree.getNode(state.tree.allData, TreeDefaultNodeIds.Root);
      return root.children?.map((item) => ({ id: item.id, title: item.title })) || [];
    },
  },
};
